import { ACTIONS, DEFAULT_BLOCKS, ESCALATIONS, TEMPLATE_FILE, TRIGGERS } from "./StageDetail";
import { pickComplete, replyButtons } from "./StageTemplatePicker";
import { BUILT_IN_RULES } from "./builtInRules";

// A test run of the Service journey for one made-up booking, with nothing sent. It follows the
// rules the API's stage engine (journey_engine.py) runs by, stage by stage, and says where a real
// booking would stop or fail: a stage that is never done, a button its template does not have,
// a template Meta has not approved, and so on.

export const wordOf = (list, key) => (list.find(([k]) => k === key) || [])[1] || key;

// True when the stage engine runs this stage (custom stages always; built-in ones once the brand is on it).
export const onEngine = (row, engine) => row.is_custom || (engine && !["created", "invites"].includes(row.stage_key));

export const duration = (seconds) => {
  const s = Number(seconds) || 0;
  if (s % 86400 === 0) return `${s / 86400} ${s === 86400 ? "day" : "days"}`;
  if (s % 3600 === 0) return `${s / 3600} ${s === 3600 ? "hour" : "hours"}`;
  if (s % 60 === 0) return `${s / 60} min`;
  return `${s} sec`;
};

export const quoted = (words) => (words || []).map((w) => `"${w}"`).join(", ");

// "Customer taps "Approve"" and "Mark done", one rule as the rules table reads it.
export function ruleLine(rule, approved) {
  const when =
    rule.source === "button"
      ? `Customer taps ${quoted([rule.button || "a button"])}`
      : `${wordOf([["customer", "Customer"], ["advisor", "Advisor"], ["staff", "Any staff"]], rule.sent_by || "advisor")} writes${rule.match === "exact" ? " exactly" : ""} ${quoted(rule.keywords) || "words"}`;
  const action = rule.action === "rating" ? `rating:${rule.rating || "experience"}` : rule.action;
  let then = wordOf(ACTIONS, action);
  if (rule.action === "status" && rule.status) then += `: ${rule.status}`;
  if (rule.action === "done" && rule.status) then += `, status ${rule.status}`;
  if (rule.action === "reply") then += `: ${approved.find((t) => t.id === rule.reply_template_id)?.name || "no message chosen"}`;
  if (rule.action === "escalate") then += `: ${wordOf(ESCALATIONS, rule.escalation) || "none chosen"}`;
  if (rule.action === "rating" && rule.score != null && rule.score !== "") then += ` ${rule.score}`;
  return { when, then, done: rule.action === "done" };
}

// What is wrong with one rule, or "" when it can run.
function ruleProblem(rule, buttons, template, usable) {
  if (rule.source === "button") {
    if (!rule.button) return "has no button chosen";
    if (!buttons.includes(rule.button)) {
      return template ? `waits for "${rule.button}", but ${template.name} has no such button` : "no template";
    }
  } else if (!(rule.keywords || []).length) {
    return "has no words to look for";
  }
  if (rule.action === "reply") {
    const reply = usable.find((t) => t.id === rule.reply_template_id);
    if (!reply) return "has no reply message chosen";
    if (reply.status !== "approved") return `replies with ${reply.name}, which Meta has not approved yet`;
  }
  if (rule.action === "status" && !(rule.status || "").trim()) return "has no status to set";
  if (rule.action === "escalate" && !rule.escalation) return "has no escalation chosen";
  if (rule.action === "rating") {
    const [low, high] = rule.rating === "recommend" ? [0, 10] : [1, 5];
    const score = Number(rule.score);
    if (rule.score == null || rule.score === "" || score < low || score > high) return `needs a score from ${low} to ${high}`;
  }
  return "";
}

// One stage on the stage engine. `startable` is false when the stage above it is never done.
function engineStep(row, above, aboveDone, pick, usable, laterStages) {
  const b = { ...DEFAULT_BLOCKS, ...(row.blocks || {}) };
  const lines = [];
  const ok = (text) => lines.push({ level: "ok", text });
  const warn = (text) => lines.push({ level: "warn", text });
  const error = (text) => lines.push({ level: "error", text });
  let never = false;

  if (b.trigger === "automatic") {
    if (!above) ok("Starts on its own");
    else if (!aboveDone) {
      error(`Never starts: ${above.label} is never done`);
      never = true;
    } else ok(`Starts after ${above.label} is done${b.delay_seconds > 0 ? `, ${duration(b.delay_seconds)} later` : ""}`);
  } else if (b.trigger === "manual") ok("Advisor presses Send on the desk");
  else ok(`Starts when the ${wordOf(TRIGGERS, b.trigger).toLowerCase()}`);

  if (b.only_if?.field) {
    if (!(b.only_if.value || "").trim()) error("\"Only send if\" has no value, so it never matches and the stage is always skipped");
    else ok(`Sent only when ${b.only_if.field === "delivery_option" ? "the delivery option" : "the service type"} is ${b.only_if.value}`);
  }

  const text = b.message_type === "text";
  const template = text ? null : usable.find((t) => t.id === pick?.id);
  const attachment = text ? (b.attachment === "album" ? "album" : "none") : template ? TEMPLATE_FILE[template.template_type] || "none" : b.attachment;
  const to = !text && b.recipient === "customer" ? "the customer" : "the group";
  if (text) {
    if ((b.text || "").trim()) ok(`Sends the text to ${to}`);
    else if (attachment === "none") warn("Sends nothing: the text is empty");
  } else if (!pick) {
    if (b.trigger === "manual" && attachment === "none") error("No template chosen: the advisor's Send fails");
    else warn("No template chosen: nothing is sent");
  } else if (!template) error("The chosen template is gone from the Templates tab");
  else if (template.status !== "approved") error(`${template.name} is not approved by Meta yet: the send fails`);
  else if (!pickComplete(pick, usable)) error(`${template.name} has fields with no value set (Templates tab): the send fails`);
  else ok(`Sends ${template.name} to ${to}`);

  if (attachment !== "none") {
    if (b.booking_file) ok(`Attaches the ${b.booking_file.replace("_", " ")} from the booking form`);
    else if (b.trigger !== "manual") warn("No booking form file: it waits until the advisor adds the file and sends it");
    else ok("The advisor adds the file");
  }

  const approved = usable.filter((t) => t.status === "approved");
  const buttons = replyButtons(template);
  let canFinish = false;
  const tapsWithoutTemplate = [];
  (b.capture_rules || []).forEach((rule, n) => {
    const problem = ruleProblem(rule, buttons, template, usable);
    const line = ruleLine(rule, approved);
    if (problem === "no template") tapsWithoutTemplate.push(n + 1);
    else if (problem) error(`Rule ${n + 1} ${problem}`);
    else {
      ok(`Test: ${line.when} → ${line.then}`);
      if (rule.action === "done") canFinish = true;
    }
  });
  if (tapsWithoutTemplate.length) {
    const n = tapsWithoutTemplate.length;
    error(`${n === 1 ? `Rule ${tapsWithoutTemplate[0]} waits` : `Rules ${tapsWithoutTemplate.join(", ")} wait`} for a button tap, but the stage sends no template with buttons`);
  }
  const waits = (b.capture_rules || []).some((r) => r.action === "done");
  let done = !never;
  if (!never && waits && !canFinish) {
    error("Stuck: no Mark done rule can run, so the stages below that start on their own never start");
    done = false;
  } else if (!never) ok(waits ? "Done by its Mark done rule" : "Done when sent");

  if (b.delete_group_minutes != null && laterStages > 0) {
    warn(`Deletes the group ${duration(b.delete_group_minutes * 60)} after this stage, but ${laterStages} ${laterStages === 1 ? "stage comes" : "stages come"} after it`);
  }
  return { lines, done, never };
}

// The test run: one step per stage, in order. A step's `level` is its worst line.
export function testRun({ rows, engine, picks, usable }) {
  const steps = [];
  let above = null;
  let aboveDone = true;
  rows.forEach((row, i) => {
    let step;
    if (!row.is_visible) {
      step = { lines: [{ level: "skip", text: "Hidden: skipped" }], done: false };
    } else if (onEngine(row, engine)) {
      const later = rows.slice(i + 1).filter((r) => r.is_visible).length;
      step = engineStep(row, above, aboveDone, picks[row.stage_key], usable, later);
    } else {
      step = { lines: [{ level: "ok", text: `Runs on the built-in code: ${(BUILT_IN_RULES[row.stage_key] || {}).trigger || "Manual"}` }], done: true };
    }
    const levels = step.lines.map((l) => l.level);
    const level = levels.includes("error") ? "error" : levels.includes("warn") ? "warn" : levels.includes("skip") ? "skip" : "ok";
    steps.push({ key: row.stage_key, label: row.label, level, lines: step.lines });
    if (row.is_visible) {
      above = row;
      aboveDone = step.done;
    }
  });
  return steps;
}
